/**
 * MediaRecorder initialization and configuration utilities
 * Handles browser-specific MediaRecorder setup with optimal codec selection
 */

import type { AudioRecorderError, MediaRecorderConfig } from '../models/audio-recorder.types';
import { AudioRecorderErrorType } from '../models/audio-recorder.types';
import { getOptimalAudioConfig, isIOS, isSafari } from './audio-recorder-browser-compat';

/**
 * Default MediaRecorder configuration
 * Optimized for voice messaging with good quality/compression balance
 */
const DEFAULT_MEDIA_RECORDER_CONFIG: MediaRecorderConfig = {
  mimeType: 'audio/webm;codecs=opus',
  audioBitsPerSecond: 32000 // 32kbps for voice is sufficient
};

/**
 * Create MediaRecorder instance with optimal configuration
 * Handles browser-specific fallbacks and codec selection
 */
export const createMediaRecorder = async (
  stream: MediaStream,
  config?: Partial<MediaRecorderConfig>
): Promise<MediaRecorder> => {
  try {
    // Get optimal audio configuration for current browser
    const optimalConfig = getOptimalAudioConfig();

    // Merge user config with optimal defaults
    const finalConfig: MediaRecorderConfig = {
      ...DEFAULT_MEDIA_RECORDER_CONFIG,
      mimeType: optimalConfig?.mimeType ?? DEFAULT_MEDIA_RECORDER_CONFIG.mimeType,
      ...config
    };

    // Validate MIME type support
    if (!MediaRecorder.isTypeSupported(finalConfig.mimeType)) {
      throw new Error(`MIME type ${finalConfig.mimeType} is not supported in this browser`);
    }

    // Create MediaRecorder with validated configuration
    const mediaRecorder = new MediaRecorder(stream, {
      mimeType: finalConfig.mimeType,
      audioBitsPerSecond: finalConfig.audioBitsPerSecond
    });

    return mediaRecorder;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw createMediaRecorderError(AudioRecorderErrorType.UNKNOWN_ERROR, errorMessage, error);
  }
};

/**
 * Get recommended timeslice for dataavailable events
 * Returns optimal chunk size in milliseconds based on browser and device
 */
export const getRecommendedTimeslice = (): number => {
  // Mobile devices: larger timeslices to reduce overhead
  if (isIOS() || /Android/i.test(navigator.userAgent)) {
    return 1000; // 1 second chunks
  }

  // Desktop browsers: smaller timeslices for better progress updates
  return 500; // 500ms chunks
};

/**
 * Start recording with proper event handler setup
 * Returns a promise that resolves when recording actually starts
 */
export const startMediaRecorder = (
  mediaRecorder: MediaRecorder,
  onDataAvailable: (event: BlobEvent) => void,
  onError?: (error: AudioRecorderError) => void
): Promise<void> => {
  return new Promise((resolve, reject) => {
    try {
      // Set up event handlers before starting
      mediaRecorder.ondataavailable = onDataAvailable;

      mediaRecorder.onerror = (event) => {
        const error = createMediaRecorderError(
          AudioRecorderErrorType.RECORDING_FAILED,
          'Recording error occurred',
          event
        );
        onError?.(error);
      };

      mediaRecorder.onstart = () => {
        resolve();
      };

      // Start recording with recommended timeslice
      const timeslice = getRecommendedTimeslice();
      mediaRecorder.start(timeslice);
    } catch (error) {
      const recordError = createMediaRecorderError(
        AudioRecorderErrorType.RECORDING_FAILED,
        error instanceof Error ? error.message : 'Failed to start recording',
        error
      );
      onError?.(recordError);
      reject(recordError);
    }
  });
};

/**
 * Stop recording and collect final data
 * Returns a promise that resolves when recording stops
 */
export const stopMediaRecorder = (mediaRecorder: MediaRecorder): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (mediaRecorder.state === 'inactive') {
      resolve();
      return;
    }

    mediaRecorder.onstop = () => {
      resolve();
    };

    try {
      mediaRecorder.stop();
    } catch (error) {
      reject(createMediaRecorderError(AudioRecorderErrorType.RECORDING_FAILED, 'Failed to stop recording', error));
    }
  });
};

/**
 * Pause recording if supported
 */
export const pauseMediaRecorder = (mediaRecorder: MediaRecorder): void => {
  if (mediaRecorder.state !== 'recording') {
    return;
  }

  try {
    mediaRecorder.pause();
  } catch (error) {
    throw createMediaRecorderError(AudioRecorderErrorType.RECORDING_FAILED, 'Failed to pause recording', error);
  }
};

/**
 * Resume recording if supported
 */
export const resumeMediaRecorder = (mediaRecorder: MediaRecorder): void => {
  if (mediaRecorder.state !== 'paused') {
    return;
  }

  try {
    mediaRecorder.resume();
  } catch (error) {
    throw createMediaRecorderError(AudioRecorderErrorType.RECORDING_FAILED, 'Failed to resume recording', error);
  }
};

/**
 * Get current MediaRecorder state
 */
export const getMediaRecorderState = (mediaRecorder: MediaRecorder | null): 'inactive' | 'recording' | 'paused' => {
  if (!mediaRecorder) return 'inactive';
  return mediaRecorder.state;
};

/**
 * Check if MediaRecorder can be paused/resumed
 * Some browsers don't support pause/resume functionality
 */
export const canPauseRecording = (): boolean => {
  // Safari on iOS doesn't support pause/resume
  if (isSafari() || isIOS()) {
    return false;
  }

  // Chrome and Firefox support pause/resume
  return true;
};

/**
 * Get recording capabilities for current browser
 */
export interface RecordingCapabilities {
  canRecord: boolean;
  canPause: boolean;
  supportedMimeTypes: string[];
  recommendedBitrate: number;
}

export const getRecordingCapabilities = (): RecordingCapabilities => {
  const optimalConfig = getOptimalAudioConfig();

  return {
    canRecord: typeof MediaRecorder !== 'undefined',
    canPause: canPauseRecording(),
    supportedMimeTypes: optimalConfig ? [optimalConfig.mimeType] : [],
    recommendedBitrate: optimalConfig?.audioBitsPerSecond ?? 32000
  };
};

/**
 * Create standardized AudioRecorderError for MediaRecorder failures
 */
const createMediaRecorderError = (
  type: AudioRecorderErrorType,
  message: string,
  originalError?: unknown
): AudioRecorderError => {
  return {
    type,
    message,
    originalError: originalError instanceof Error ? originalError : undefined,
    recoverable: type === AudioRecorderErrorType.RECORDING_FAILED
  };
};

/**
 * Cleanup MediaRecorder and release resources
 */
export const cleanupMediaRecorder = (mediaRecorder: MediaRecorder | null, stream: MediaStream | null): void => {
  // Stop recording if active
  if (mediaRecorder && mediaRecorder.state !== 'inactive') {
    try {
      mediaRecorder.stop();
    } catch {
      // Ignore errors during cleanup
    }
  }

  // Clear event handlers
  if (mediaRecorder) {
    mediaRecorder.ondataavailable = null;
    mediaRecorder.onerror = null;
    mediaRecorder.onstart = null;
    mediaRecorder.onstop = null;
    mediaRecorder.onpause = null;
    mediaRecorder.onresume = null;
  }

  // Stop all media stream tracks
  if (stream) {
    stream.getTracks().forEach((track) => {
      track.stop();
    });
  }
};
